import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Check } from 'lucide-react';
import { requestAndSubscribe, getNotificationStatus } from '../services/notificationService';
import './NotificationPermissionBanner.css';

const BANNER_DISMISSED_KEY = 'jogolinga-notif-banner-snoozed';
const SNOOZE_DAYS = 14;

const isSnoozed = () => {
  const val = localStorage.getItem(BANNER_DISMISSED_KEY);
  if (!val) return false;
  const snoozedAt = parseInt(val, 10);
  return Date.now() - snoozedAt < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
};

const NotificationPermissionBanner: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const hasUsed = localStorage.getItem('hasEverUsedApp') === 'true';
    if (isSnoozed() || !hasUsed) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    getNotificationStatus().then(status => {
      if (status !== 'granted') {
        timer = setTimeout(() => setVisible(true), 4000);
      }
    });
    return () => clearTimeout(timer);
  }, []);

  const handleActivate = async () => {
    setLoading(true);
    const granted = await requestAndSubscribe();
    setLoading(false);
    if (granted) setDone(true);
    localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now()));
    setTimeout(() => setVisible(false), granted ? 1800 : 0);
  };

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  const banner = (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="npb-banner"
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          role="dialog"
          aria-label={t('notifBanner.ariaLabel')}
        >
          <img src="/Mascotte/mascotte5.png" alt="" className="npb-mascot" />

          {done ? (
            <div className="npb-content">
              <p className="npb-text">
                <strong className="npb-success"><Check size={15} strokeWidth={2.5} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{t('notifBanner.activatedTitle')}</strong>
                <br />
                <span>{t('notifBanner.activatedDesc')}</span>
              </p>
            </div>
          ) : (
            <div className="npb-content">
              <p className="npb-text">
                <strong><Flame size={15} strokeWidth={1.75} fill="currentColor" style={{ verticalAlign: '-2px', marginRight: '4px' }} />{t('notifBanner.title')}</strong>
                <br />
                <span>{t('notifBanner.desc')}</span>
              </p>
              <div className="npb-actions">
                <button
                  className="npb-btn npb-btn--primary"
                  onClick={handleActivate}
                  disabled={loading}
                >
                  {loading ? '…' : t('notifBanner.activate')}
                </button>
                <button className="npb-btn npb-btn--ghost" onClick={handleDismiss}>
                  {t('notifBanner.later')}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Portal vers document.body pour échapper aux transforms parents (Framer Motion)
  // qui casseraient le positionnement fixed
  return ReactDOM.createPortal(banner, document.body);
};

export default NotificationPermissionBanner;
